import React, { useCallback, useEffect, useRef, useState } from "react";
import { subscribeScrollLock } from "../../utils/bodyScrollLock";
import "./documentScrollbar.css";

/**
 * DocumentScrollbar — an overlay scrollbar for the PAGE, and only the page.
 *
 * ─────────────────────────────────────────────────────────────────
 * WHY IT EXISTS. A native scrollbar occupies layout width. That one fact
 * caused three separate problems:
 *
 *   · The 16px lane is a strip of page the content cannot use, and its track
 *     is transparent, so it showed the CANVAS — which, because this app's
 *     <html> box is ~0px tall, could never carry the app's gradient. (See the
 *     comment in public/index.html for that whole mechanism.) A NESTED
 *     scrollbar has never had this problem: its transparent track shows the
 *     panel it sits on. This component gives the page the same property by
 *     removing the lane entirely.
 *   · `overflow: hidden` on a modal open REMOVES that lane, so the viewport
 *     widens by 16px and everything centred jumps. Nothing to remove now.
 *   · Blink does not animate CSS transitions on `::-webkit-scrollbar-*`
 *     pseudo-elements, so a native thumb can only snap between colours. This
 *     is a real element, so `opacity` is just an animatable property.
 *
 * ─────────────────────────────────────────────────────────────────
 * SCOPED TO THE DOCUMENT ON PURPOSE. The app has ~74 scrolling containers.
 * Hiding scrollbars globally would strand every one of them until it was
 * wrapped. Nested scrollers keep their native capsule — they sit inside panels
 * where a lane costs nothing, and the `--bdr-scrollbar-w` <-> `.week-grid-header`
 * offset depends on that lane existing. Only `html::-webkit-scrollbar` is
 * hidden; the bare rules still dress everything else.
 *
 * ─────────────────────────────────────────────────────────────────
 * IT IS NOT READ-ONLY. A projection that only reflects position removes
 * drag-to-scroll, which is the one thing people actually use a desktop
 * scrollbar for. Pointer capture + a delta on `scrollTop` is the whole cost.
 *
 * WHAT IT DOES NOT REPLACE: click-in-track to page, and middle-click
 * autoscroll. Both are OS behaviours of the native widget. Wheel, keyboard,
 * touch and trackpad are untouched — the page still scrolls natively; only the
 * indicator is ours.
 */

/* PublicV2Layout puts this on <body> while a public page is mounted. */
const V2_CLASS = "bdrv2-active";
/* Gates the CSS that hides the native bar — see index.css. */
const ENABLED_CLASS = "bdr-overlay-scroll";

const MIN_THUMB = 28;
const IDLE_MS = 1400;

const DocumentScrollbar = () => {
  const [metrics, setMetrics] = useState(null); // { top, height } | null
  const [active, setActive] = useState(false);
  const [locked, setLocked] = useState(false);
  const [v2Active, setV2Active] = useState(false);
  const railRef = useRef(null);
  const idleTimer = useRef(null);
  const dragging = useRef(null); // { startY, startScroll, usable, range }
  const rafId = useRef(0);

  /* Measures the document and derives the thumb. `null` means "not scrollable"
     — the bar unmounts rather than rendering a full-height thumb that cannot
     move, which reads as a broken control. */
  const measure = useCallback(() => {
    const doc = document.documentElement;
    const view = doc.clientHeight;
    const total = doc.scrollHeight;

    /* +1 absorbs sub-pixel layout, which otherwise leaves a permanent bar on
       pages that are a fraction of a pixel too tall. */
    if (total <= view + 1) {
      setMetrics(null);
      return;
    }

    /*
     * TRAVEL IS THE RAIL'S HEIGHT, NOT THE VIEWPORT'S — measured off the rail
     * element itself. The rail is inset from the top and bottom of the window
     * so the thumb never touches the edges, and that inset lives ONLY in
     * documentScrollbar.css. Computing against `clientHeight` here would hard-
     * code the same number in two places and push the thumb past the bottom of
     * its own rail the moment either changed. This way the CSS can be retuned
     * freely and the maths follows it.
     */
    const rail = railRef.current ? railRef.current.clientHeight : view;
    const height = Math.max(MIN_THUMB, (view / total) * rail);
    const range = total - view;
    const progress = range > 0 ? Math.min(1, doc.scrollTop / range) : 0;
    setMetrics({ top: progress * (rail - height), height });
  }, []);

  /* Coalesced to one measure per frame: a flick fires ~50 scroll events and
     each one would otherwise be a layout read plus a React render. */
  const schedule = useCallback(() => {
    if (rafId.current) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = 0;
      measure();
    });
  }, [measure]);

  const wake = useCallback(() => {
    setActive(true);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setActive(false), IDLE_MS);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      schedule();
      wake();
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", schedule);

    /*
     * The page's height changes without any scroll or resize event — a list
     * loads, a disclosure opens. `document.body` is NOT the thing to watch:
     * every child of #root is out of flow here, so body measures ~0 and would
     * never fire. `.content` is the element that actually has the height.
     */
    const target = document.querySelector(".content") || document.body;
    const ro = new ResizeObserver(schedule);
    ro.observe(target);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", schedule);
      ro.disconnect();
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [measure, schedule, wake]);

  /*
   * While a modal holds the scroll lock the page cannot move, but its
   * `scrollHeight` is unchanged — so `measure` would happily keep drawing a
   * thumb, floating over the modal's backdrop, responding to a drag that can
   * scroll nothing. The lock is the only reliable signal for this: no scroll,
   * resize or content event fires when a modal opens.
   */
  useEffect(() => subscribeScrollLock(setLocked), []);

  /*
   * STAND DOWN ON THE PUBLIC V2 PAGES. They style the document scrollbar
   * themselves and are not part of the admin design system — an admin-green
   * overlay has no business on a marketing page. `PublicV2Layout` signals this
   * by adding `bdrv2-active` to <body>, so watching that attribute is enough
   * and this component needs no knowledge of routes.
   *
   * While stood down the ENABLED_CLASS comes off <html>, which restores the
   * native bar and hands V2's own rules back their effect.
   */
  useEffect(() => {
    const body = document.body;
    const sync = () => setV2Active(body.classList.contains(V2_CLASS));
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(body, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, []);

  /*
   * ONLY the public V2 pages stand this down from JS now. The mobile case is a
   * WIDTH, handled entirely in documentScrollbar.css, where hiding the native
   * bar and showing the overlay sit in ONE media block and cannot disagree.
   * Deciding it here from pointer/hover media queries was tried twice and left
   * mobile with no scrollbar at all both times, because the CSS half and the
   * JS half each assumed the other was handling it.
   */
  const standDown = v2Active;

  useEffect(() => {
    const root = document.documentElement;
    if (standDown) {
      root.classList.remove(ENABLED_CLASS);
    } else {
      root.classList.add(ENABLED_CLASS);
    }
    return () => root.classList.remove(ENABLED_CLASS);
  }, [standDown]);

  const onPointerDown = (event) => {
    if (!metrics) return;
    event.preventDefault();
    /* Capture so the drag survives the pointer leaving the 10px rail — without
       it, moving a few px sideways mid-drag drops the gesture. */
    event.currentTarget.setPointerCapture(event.pointerId);
    const doc = document.documentElement;
    dragging.current = {
      startY: event.clientY,
      startScroll: doc.scrollTop,
      usable:
        (railRef.current ? railRef.current.clientHeight : doc.clientHeight) -
        metrics.height,
      range: doc.scrollHeight - doc.clientHeight,
    };
    wake();
  };

  const onPointerMove = (event) => {
    const d = dragging.current;
    if (!d || d.usable <= 0) return;
    /* Thumb travel maps onto scroll range, not 1:1 with the pointer. */
    const delta = ((event.clientY - d.startY) / d.usable) * d.range;
    document.documentElement.scrollTop = d.startScroll + delta;
    wake();
  };

  const endDrag = (event) => {
    if (!dragging.current) return;
    dragging.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  /*
   * THE RAIL ALWAYS RENDERS; only the thumb comes and goes. `measure` reads the
   * rail's height, so unmounting the rail when there is nothing to scroll would
   * leave nothing to measure on the first pass and metrics could never be
   * produced — the bar would never appear at all. It is `pointer-events: none`
   * and paints nothing, so an empty rail costs exactly one div.
   */
  const showThumb = !!metrics && !locked && !standDown;

  return (
    /* `aria-hidden`: the page is already scrollable by every native means, so
       this is a duplicate control that would only add noise to a screen
       reader. It is decoration over real behaviour. */
    <div className="doc-scrollbar" ref={railRef} aria-hidden="true">
      {showThumb && (
        <div
          className={`doc-scrollbar-thumb${active ? " is-active" : ""}`}
          style={{ top: `${metrics.top}px`, height: `${metrics.height}px` }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        />
      )}
    </div>
  );
};

export default DocumentScrollbar;
